import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import styled from 'styled-components';
import { Classes } from '@blueprintjs/core';

import Text from 'core/components/Text';
import Box from 'core/components/Box';
import NotificationUsageListContainer from './NotificationUsageListContainer';

const UsageLinkContainer = styled(Box)`
  cursor: pointer;
  padding: 4px;

  &:hover {
    background-color: ${({ theme }) => theme.backgrounds.specialMuted}};
  }
`;

@inject('$alerting', '$mitigations', '$syn', '$mkp')
@observer
export default class NotificationUsageList extends Component {
  static propTypes = {
    showUnknownSource: PropTypes.bool,
    notificationModel: PropTypes.object.isRequired
  };

  static defaultProps = {
    showUnknownSource: false
  };

  state = {
    detailsDialogOpen: false
  };

  get usagesLoaded() {
    const { $alerting, $mitigations, $syn } = this.props;
    const { policyCollection } = $alerting;
    const { methodsCollection } = $mitigations;
    const { tests } = $syn;

    return tests.hasFetched && policyCollection.hasFetched && methodsCollection.hasFetched;
  }

  toggleDetailDialog = () => {
    this.setState((state) => ({
      detailsDialogOpen: !state.detailsDialogOpen
    }));
  };

  get usageTexts() {
    if (!this.usagesLoaded) {
      return [];
    }

    const { notificationModel } = this.props;
    const summary = notificationModel.usedBySummary;
    const texts = [];

    if (summary.policies.length > 0) {
      texts.push(`Policies (${summary.policies.length})`);
    }

    if (summary.insights.length > 0) {
      texts.push(`Insights (${summary.insights.length})`);
    }

    if (summary.insightFamilies.length > 0) {
      texts.push(`Insight Families (${summary.insightFamilies.length})`);
    }

    if (summary.mitigationMethod.length > 0) {
      texts.push(`Mitigation Methods (${summary.mitigationMethod.length})`);
    }

    if (summary.synthTest.length > 0) {
      texts.push(`Synthetic Tests (${summary.synthTest.length})`);
    }

    if (summary.subpolicies.length > 0) {
      const subpolicyCount = summary.subpolicies.flat().length;
      texts.push(`Tenant Policies (${subpolicyCount})`);
    }

    if (summary.aiInvestigation.length > 0) {
      texts.push(`Auto Investigation (${summary.aiInvestigation.length})`);
    }

    if (summary.unknown.length > 0) {
      texts.push(`Other (${summary.unknown.length})`);
    }

    return texts;
  }

  render() {
    const { notificationModel, inline, asTable, addDeleteColumn, showUnknownSource } = this.props;
    const { detailsDialogOpen } = this.state;

    const showContainer = asTable || detailsDialogOpen;

    return (
      <>
        {showContainer && (
          <NotificationUsageListContainer
            notificationModel={notificationModel}
            asTable={asTable}
            addDeleteColumn={addDeleteColumn}
            loading={!this.usagesLoaded}
            isOpen={detailsDialogOpen}
            onClose={this.toggleDetailDialog}
          />
        )}
        {!asTable && (
          <Box
            display={inline ? 'inline-block' : 'block'}
            showSkeleton={!this.usagesLoaded}
            width={!this.usagesLoaded ? 100 : 'auto'}
          >
            {this.usageTexts.length > 0 ? (
              <UsageLinkContainer onClick={this.toggleDetailDialog} className={Classes.TEXT_OVERFLOW_ELLIPSIS}>
                {this.usageTexts.map((usageText, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Text key={idx}>
                    {usageText}
                    {this.usageTexts.length - 1 > idx ? ', ' : ''}
                  </Text>
                ))}
              </UsageLinkContainer>
            ) : (
              ''
            )}
            {this.usageTexts.length === 0 && this.usagesLoaded ? (
              <Text ml="4px" muted>
                {showUnknownSource ? 'Unknown' : 'Not in use'}
              </Text>
            ) : (
              ''
            )}
          </Box>
        )}
      </>
    );
  }
}
